import { useRef, useState, useCallback, useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useMergeValue } from '../../../hooks/useMergeValue.js';
import { on, off } from '../../../utils/dom.js';
import { Esc } from '../../../utils/keycode.js';

const useModal = ({
  visible,
  confirmLoading,
  onCancel,
  mask,
  onConfirm,
  onOk
}) => {
  const modalWrapperRef = useRef(null);
  const contentWrapper = useRef(null);
  const keyboardEventOn = useRef(false);
  const [popupZIndex, setPopupZIndex] = useState();
  const maskClickRef = useRef(false);
  const inExit = useRef(false);
  const [loading, setLoading] = useMergeValue(false, {
    defaultValue: false,
    value: confirmLoading
  });

  // Overlay
  const overlayStyle = useSpring({
    opacity: visible ? '1' : '0',
    reset: true,
    delay: 30
  });
  const springConfig = {
    from: {
      opacity: 0,
      transform: 'translateY(-40px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0px)'
    },
    leave: {
      opacity: 0,
      transform: 'translateY(-40px)'
    }
  };
  const handleOnCancel = useCallback(() => {
    return onCancel && onCancel();
  }, [onCancel]);
  const onClickMask = e => {
    if (!maskClickRef.current) return;
    maskClickRef.current = false;
    if (!inExit.current && mask && e.target === e.currentTarget) {
      setTimeout(() => {
        handleOnCancel();
      }, 100);
    }
  };
  const onConfirmModal = e => {
    const handleOnConfirm = onOk || onConfirm;
    let ret;
    if (handleOnConfirm) {
      ret = handleOnConfirm(e);
    }
    if (ret && ret.then) {
      setLoading(true);
      ret.then(() => {
        setLoading(false);
      }, e => {
        setLoading(false);
        console.error(e);
      });
    }
  };
  useEffect(() => {
    const onKeyDown = e => {
      if (e && e.code === Esc.key) {
        handleOnCancel();
      }
    };
    if (visible && !keyboardEventOn.current) {
      keyboardEventOn.current = true;
      on(document, 'keydown', onKeyDown);
    }
    return () => {
      keyboardEventOn.current = false;
      off(document, 'keydown', onKeyDown);
    };
  }, [handleOnCancel, visible]);
  useEffect(() => {
    if (visible && popupZIndex === undefined && modalWrapperRef.current) {
      const zIndex = Number(window.getComputedStyle(modalWrapperRef.current, null)?.zIndex);
      if (!Number.isNaN(zIndex)) {
        setPopupZIndex(zIndex + 1);
      }
    }
  }, [visible, popupZIndex]);
  return {
    springConfig,
    handleOnCancel,
    modalWrapperRef,
    overlayStyle,
    inExit,
    maskClickRef,
    onConfirmModal,
    onClickMask,
    loading,
    contentWrapper
  };
};

export { useModal };
